/**
 * src/components/shared/ComposeEmailModal.jsx — TICKET-206 / TICKET-email-photo-attachments
 *
 * Free-form email composer with optional "Show similar work" photo picker. The
 * CEO selects example/work photos from the vessel's gallery; they are attached to
 * the outbound email as MIME parts (server-side, via leads.send_email attachment_ids).
 *
 * Props:
 *   open             — boolean, controls visibility
 *   onClose          — () => void
 *   defaultTo        — string, prefill recipient
 *   defaultSubject   — string, prefill subject
 *   dealProductTypes — string[], defaults the picker's product-type filter
 *   dealScope        — { project_id?, deal_id?, vessel_id? } passed to listPhotos
 */
import { useState, useEffect, useCallback } from 'react';
import { Send, Image, X as XIcon } from 'lucide-react';
import { ModalSheet } from '../SharedUI';
import { useT } from '../../lib/i18n/I18nContext';
import { sendGmailEmail } from '../../lib/api/leads.js';
import { listPhotos } from '../../lib/api/photos.js';
import { thumbUrlFromDoc } from '../../lib/api/documents.js';
import AiDraftBlock from './AiDraftBlock';
import useUndoableSend from '../../lib/hooks/useUndoableSend';
import { readDraft } from '../../lib/undoSend';

/** Draft slot for the free composer — where a crash recovery lands (TICKET-858). */
const draftKey = (dealId) => `email-draft-compose-${dealId || 'global'}`;

/** Server's "no Gmail source wired" signal — retrying can't help, so tell the truth. */
function isNotConfigured(error) {
  if (!error || typeof error !== 'string') return false;
  return /not configured|GMAIL_SA_KEY|GMAIL_LEAD_MAILBOX|no lead source configured/i.test(error);
}

/** Derive a display label from a photo row (summary or file_name). */
function photoLabel(doc) {
  return doc.summary || doc.file_name || doc.id.slice(0, 8);
}

/** Thumbnail URL from metadata — shared derivation (TICKET-459). */
const thumbFrom = thumbUrlFromDoc;

export default function ComposeEmailModal({
  open,
  onClose,
  defaultTo = '',
  defaultSubject = '',
  dealProductTypes = [],
  dealScope = {},
  dealId = null,
}) {
  const { t } = useT();
  const undoableSend = useUndoableSend();

  // Form state
  const [to, setTo] = useState(defaultTo);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState('');

  // Photo picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  // Refresh fields whenever the modal (re)opens or its prefill changes. TICKET-858:
  // a saved draft wins over the prefill — it only exists after an Undo, a failed
  // send or a crash recovery, and in all three cases it is the user's own words.
  // Attachments are deliberately NOT restored: the picker's photo ids are cheap to
  // re-pick and restoring ids whose documents may have moved would be a lie.
  useEffect(() => {
    if (!open) return;
    const saved = readDraft(draftKey(dealId));
    setTo(saved?.to || defaultTo);
    setSubject(saved?.subject || defaultSubject);
    setBody(saved?.body || '');
    setSelectedIds([]);
    setPickerOpen(false);
    setPhotos([]);
  }, [open, dealId, defaultTo, defaultSubject]);

  // Load photos when picker opens.
  useEffect(() => {
    if (!pickerOpen) return;
    let alive = true;
    setPhotosLoading(true);
    listPhotos({
      scope: dealScope,
      product_types: dealProductTypes.length ? dealProductTypes : [],
    })
      .then((res) => { if (alive) setPhotos(res?.documents || []); })
      .catch(() => { if (alive) setPhotos([]); })
      .finally(() => { if (alive) setPhotosLoading(false); });
    return () => { alive = false; };
  }, [pickerOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePhoto = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  // TICKET-1177 (CEO 2026-10-14: "require text"): the server refuses an empty body
  // (TICKET-1170), so a photos-only send used to close the sheet and fail 30s later.
  const canSend = !!to.trim() && !!body.trim();

  // TICKET-855: the same 30s undo window + spoken outcome as the History reply
  // composer — one send experience, so neither surface can drift into silence.
  const onSend = useCallback(() => {
    const cleanTo = to.trim();
    if (!cleanTo || !body.trim()) return;
    undoableSend(
      () => sendGmailEmail({
        to: cleanTo,
        subject,
        body,
        ...(selectedIds.length ? { attachment_ids: selectedIds } : {}),
      }),
      {
        to: cleanTo,
        mapError: (res) => (isNotConfigured(res?.error) ? t('composeEmail.notConfigured') : null),
        snapshot: { draftKey: draftKey(dealId), to: cleanTo, subject, body },
      },
    );
    onClose?.();
  }, [to, subject, body, selectedIds, dealId, undoableSend, onClose, t]);

  const inputStyle = {
    width: '100%', minHeight: 44, borderRadius: 8, fontSize: 13,
    padding: '10px 12px', outline: 'none', boxSizing: 'border-box',
    background: 'var(--card)', color: 'var(--t1)',
    boxShadow: 'inset 0 0 0 1px var(--bdr)',
  };
  const labelStyle = {
    fontSize: 12, fontWeight: 600, color: 'var(--t2)', marginBottom: 4, display: 'block',
  };

  return (
    <ModalSheet open={open} onClose={onClose} title={t('composeEmail.title')} id="compose-email">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: '4px 2px' }}>
        <div>
          <label style={labelStyle} htmlFor="compose-to">{t('composeEmail.to')}</label>
          <input
            id="compose-to" data-testid="compose-email-to" type="email"
            value={to} onChange={(e) => setTo(e.target.value)}
            placeholder={t('composeEmail.toPlaceholder')} style={inputStyle} autoComplete="off"
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="compose-subject">{t('composeEmail.subject')}</label>
          <input
            id="compose-subject" data-testid="compose-email-subject" type="text"
            value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder={t('composeEmail.subjectPlaceholder')} style={inputStyle}
          />
        </div>
        {/* Unified AI-draft assist (notes + language + mic + Draft-with-AI). Shared
            with the History-tab reply composer via AiDraftBlock — improve once,
            improves everywhere. Writes the draft to the body; notes stay put. */}
        {dealId && (
          <AiDraftBlock
            dealId={dealId}
            hasBody={!!body}
            onDraft={(d) => { if (d.subject) setSubject(d.subject); if (d.body) setBody(d.body); }}
          />
        )}
        <div>
          <label style={labelStyle} htmlFor="compose-body">{t('composeEmail.body')}</label>
          <textarea
            id="compose-body" data-testid="compose-email-body"
            value={body} onChange={(e) => setBody(e.target.value)}
            placeholder={t('composeEmail.bodyPlaceholder')} rows={6}
            style={{ ...inputStyle, minHeight: 120, resize: 'vertical', lineHeight: 1.5 }}
          />
        </div>

        {/* ── Photo picker ──────────────────────────────────────────── */}
        <div>
          <button
            type="button"
            data-testid="compose-email-photo-picker-toggle"
            onClick={() => setPickerOpen((v) => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              minHeight: 44, padding: '8px 14px', borderRadius: 10, border: 'none',
              background: pickerOpen ? 'var(--navy)' : 'var(--glass)',
              color: pickerOpen ? 'var(--card)' : 'var(--t2)',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              boxShadow: pickerOpen ? 'none' : 'inset 0 0 0 1px var(--bdr)',
            }}
          >
            <Image size={16} strokeWidth={1.75} />
            {pickerOpen ? t('composeEmail.photoPickerClose') : t('composeEmail.photoPicker')}
          </button>

          {pickerOpen && (
            <div style={{ marginTop: 10 }}>
              {photosLoading && (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--tm)' }}>Loading…</p>
              )}
              {!photosLoading && photos.length === 0 && (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--tm)' }}>
                  {t('composeEmail.photoPickerEmpty')}
                </p>
              )}
              {!photosLoading && photos.length > 0 && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                  gap: 8,
                }}>
                  {photos.map((doc) => {
                    const isSelected = selectedIds.includes(doc.id);
                    const thumb = thumbFrom(doc);
                    return (
                      <button
                        key={doc.id}
                        type="button"
                        data-testid="compose-email-photo-tile"
                        data-id={doc.id}
                        aria-pressed={isSelected}
                        onClick={() => togglePhoto(doc.id)}
                        title={photoLabel(doc)}
                        style={{
                          position: 'relative', padding: 0, borderRadius: 8, border: 'none',
                          background: 'var(--glass)', overflow: 'hidden', cursor: 'pointer',
                          minHeight: 44, height: 80,
                          outline: isSelected ? '2px solid var(--navy)' : '1px solid var(--bdr)',
                          outlineOffset: isSelected ? -2 : 0,
                        }}
                      >
                        {thumb
                          ? <img src={thumb} alt={photoLabel(doc)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <Image size={20} color="var(--tm)" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }} />}
                        {isSelected && (
                          <span style={{
                            position: 'absolute', inset: 0,
                            background: 'rgba(0,31,63,0.35)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <span style={{
                              width: 20, height: 20, borderRadius: 999,
                              background: 'var(--navy-surface)', border: '2px solid var(--card)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <XIcon size={10} color="var(--card)" strokeWidth={3} />
                            </span>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Attachment chips ─────────────────────────────────────── */}
        {selectedIds.length > 0 && (
          <div>
            <span style={{ ...labelStyle, marginBottom: 6 }}>
              {t('composeEmail.attachmentsLabel', { count: selectedIds.length })}
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {selectedIds.map((id) => {
                const doc = photos.find((p) => p.id === id);
                return (
                  <button
                    key={id}
                    type="button"
                    data-testid={`compose-email-attachment-chip-${id}`}
                    onClick={() => togglePhoto(id)}
                    title={t('composeEmail.removeAttachment')}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '4px 10px', borderRadius: 999, border: 'none',
                      background: 'var(--stodob)', color: 'var(--t1)',
                      fontSize: 12, fontWeight: 500, cursor: 'pointer',
                      minHeight: 44,
                    }}
                  >
                    <Image size={12} strokeWidth={1.75} />
                    {doc ? photoLabel(doc).slice(0, 20) : id.slice(0, 8)}
                    <XIcon size={12} strokeWidth={2} />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Action row ───────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
          <button
            type="button" data-testid="compose-email-cancel" onClick={() => onClose?.()}
            style={{
              minHeight: 44, padding: '8px 16px', borderRadius: 10, border: 'none',
              background: 'transparent', color: 'var(--t2)', fontSize: 14, fontWeight: 500,
              cursor: 'pointer', boxShadow: 'inset 0 0 0 1px var(--bdr)',
            }}
          >{t('composeEmail.cancel')}</button>
          <button
            type="button" data-testid="compose-email-send" onClick={onSend}
            disabled={!canSend}
            style={{
              minHeight: 44, padding: '8px 18px', borderRadius: 10, border: 'none',
              display: 'flex', alignItems: 'center', gap: 8,
              background: canSend ? 'var(--accent)' : 'var(--accent-bg)',
              color: canSend ? 'var(--card)' : 'var(--accent)',
              fontSize: 14, fontWeight: 600, cursor: canSend ? 'pointer' : 'default',
              opacity: canSend ? 1 : 0.6, transition: 'all 150ms var(--ease)',
            }}
          >
            <Send size={18} strokeWidth={1.75} />
            {t('composeEmail.send')}
          </button>
        </div>
      </div>
    </ModalSheet>
  );
}
